// The workflow editor's hash. Links are shared, so a change here must keep reading the addresses already out there.

export const BLOCK_ADDRESS_PREFIX = 'block-'
export const CONDITION_GROUP_ADDRESS_PREFIX = 'condition-group-'
const FORM_SEGMENT_PREFIX = 'form-'
const ITEM_SEGMENT_PREFIX = 'item-'
const SECTION_SEGMENT_PREFIX = 'section-'
const CONDITION_SEGMENT = 'condition'

/** A block's edit panel section that an address can scroll to. */
const PANEL_SECTIONS = ['assignees', 'duration', 'tasks'] as const
export type PanelSection = (typeof PANEL_SECTIONS)[number]

// At most one of `formTemplateId`, `section` and `condition`: each names where inside the block the address leads.
// `itemId` (a question or section of the form) only goes with `formTemplateId`.
export type BlockAddress = {
  blockId: string
  formTemplateId?: string
  itemId?: string
  section?: PanelSection
  condition?: true
}

const segmentValue = (segment: string | undefined, prefix: string) =>
  segment?.startsWith(prefix) && segment.length > prefix.length ? segment.slice(prefix.length) : undefined

const isPanelSection = (value: string | undefined): value is PanelSection => PANEL_SECTIONS.some((section) => section === value)

export const parseBlockAddress = (fragment: string | undefined): BlockAddress | null => {
  const blockId = segmentValue(fragment?.split('/')[0], BLOCK_ADDRESS_PREFIX)
  if (!fragment || !blockId) return null

  const [, innerSegment, itemSegment] = fragment.split('/')
  if (innerSegment === CONDITION_SEGMENT) return { blockId, condition: true }

  const section = segmentValue(innerSegment, SECTION_SEGMENT_PREFIX)
  if (isPanelSection(section)) return { blockId, section }

  const formTemplateId = segmentValue(innerSegment, FORM_SEGMENT_PREFIX)
  if (!formTemplateId) return { blockId }

  const itemId = segmentValue(itemSegment, ITEM_SEGMENT_PREFIX)
  return itemId ? { blockId, formTemplateId, itemId } : { blockId, formTemplateId }
}

export const buildBlockAddress = ({ blockId, formTemplateId, itemId, section, condition }: BlockAddress): string => {
  const segments = [`${BLOCK_ADDRESS_PREFIX}${blockId}`]
  if (formTemplateId) {
    segments.push(`${FORM_SEGMENT_PREFIX}${formTemplateId}`)
    if (itemId) segments.push(`${ITEM_SEGMENT_PREFIX}${itemId}`)
  } else if (section) segments.push(`${SECTION_SEGMENT_PREFIX}${section}`)
  else if (condition) segments.push(CONDITION_SEGMENT)
  return segments.join('/')
}

export const buildConditionGroupAddress = (groupId: string): string => `${CONDITION_GROUP_ADDRESS_PREFIX}${groupId}`
